// Period / Value / RAG / Logged / Evidence history table for the KPI
// detail drawer. Newest-first, 5 rows collapsed by default with a
// "Show all" expansion. Per-row amend opens the unified log-period
// modal pre-filled (caller-supplied onAmend handler receives the
// current row's initial data). Includes CSV export + a truncation banner.

import { useMemo, useState, type JSX } from "react";
import { Pencil } from "lucide-react";
import Alert from "../../../../common/Alert";
import Button from "../../../../common/Button";
import TrackingStatusBadge from "../../shared/TrackingStatusBadge";
import RaiseIssueButton from "../../shared/RaiseIssueButton";
import EvidenceLinkDisplay from "../evidence/EvidenceLinkDisplay";
import type {
  EvidenceLink,
  Kpi,
  KpiMeasurement,
  KpiReport,
  RagRating,
  UserDisplay,
} from "../../../../../types";
import { formatPeriodLabel } from "../../../../../lib/kpi/period";
import type { KpiFrequency } from "../../../../../lib/kpi/period";
import { formatKpiValue } from "../formatKpiValue";
import { useUserDisplays } from "../../../../../hooks/useUsers";

export type KpiHistoryRange = "30d" | "90d" | "12m" | "ytd";

export interface KpiHistoryAmendData {
  period: string;
  value?: string;
  ragRating?: RagRating;
  commentary?: string;
  evidenceLinks?: EvidenceLink[];
  /** Existing measurement id for the period — used by the amend flow to
   *  replace (update) rather than create a duplicate. Undefined when no
   *  measurement exists for the row (report-only period). */
  measurementId?: string;
  /** Existing report id for the period — used by the amend flow to
   *  replace (update) rather than create a duplicate. Undefined when no
   *  report exists for the row (measurement-only period). */
  reportId?: string;
}

export interface KpiHistoryTableProps {
  kpi: Kpi;
  measurements: KpiMeasurement[];
  reports: KpiReport[];
  range: KpiHistoryRange;
  isTruncated?: boolean;
  onAmend: (data: KpiHistoryAmendData) => void;
}

const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

// `evidenceLinks` is the only extra field read off a report without
// widening KpiReport itself. User attribution (previously faked via
// `updatedByName` / `updatedByOrg`) is now resolved via
// `useUserDisplays(updatedBy)`.
interface ExtendedReport extends KpiReport {
  evidenceLinks?: EvidenceLink[];
}

function periodYear(period: string): string | null {
  const m = /^(\d{4})/u.exec(period);
  return m ? m[1] : null;
}

function formatLoggedCell(
  report: KpiReport | undefined,
  resolvedUser: UserDisplay | undefined,
): string {
  if (!report) return "—";
  const updated = report.updatedAt ? new Date(report.updatedAt) : null;
  if (!updated || Number.isNaN(updated.getTime())) return "—";
  const day = pad2(updated.getUTCDate());
  const month = MONTH_LABELS[updated.getUTCMonth()];
  const year = updated.getUTCFullYear();
  const periodY = periodYear(report.period);
  const dateText =
    periodY && periodY === String(year)
      ? `${day} ${month}`
      : `${day} ${month} ${year}`;
  const name = resolvedUser?.name?.trim();
  if (!name) return dateText;
  const parts = name.split(/\s+/u).filter(Boolean);
  const compact =
    parts.length === 1
      ? parts[0]
      : `${parts[0]} ${parts[parts.length - 1][0]}.`;
  return `${dateText} · ${compact}`;
}

function fullNameTooltip(
  resolvedUser: UserDisplay | undefined,
): string | undefined {
  if (!resolvedUser?.name?.trim()) return undefined;
  const org = resolvedUser.organisation?.trim();
  return org
    ? `${resolvedUser.name} (${org})`
    : resolvedUser.name;
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/\s+/gu, "-")
    .replace(/[^a-z0-9-]/gu, "")
    .slice(0, 40);
}

function isoDateUTC(d: Date): string {
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

export default function KpiHistoryTable({
  kpi,
  measurements,
  reports,
  range,
  isTruncated = false,
  onAmend,
}: KpiHistoryTableProps): JSX.Element {
  const [showAll, setShowAll] = useState(false);

  // Merge by period key — newest first.
  const periods = useMemo(() => {
    const perPeriod = new Map<
      string,
      { measurement?: KpiMeasurement; report?: KpiReport }
    >();
    for (const m of measurements) {
      const bucket = perPeriod.get(m.period) ?? {};
      bucket.measurement = m;
      perPeriod.set(m.period, bucket);
    }
    for (const r of reports) {
      const bucket = perPeriod.get(r.period) ?? {};
      bucket.report = r;
      perPeriod.set(r.period, bucket);
    }
    return Array.from(perPeriod.entries())
      .map(([period, v]) => ({ period, ...v }))
      .sort((a, b) => b.period.localeCompare(a.period));
  }, [measurements, reports]);

  // Batch-resolve every distinct `updatedBy` across the visible rows
  // so the Logged column renders "{DD MMM} · {First L.}" with one
  // round-trip per drawer open, not per row.
  const updatedByIds = useMemo(
    () =>
      Array.from(
        new Set(
          periods
            .map((p) => p.report?.updatedBy)
            .filter((id): id is string => Boolean(id)),
        ),
      ),
    [periods],
  );
  const userLookup = useUserDisplays(updatedByIds);
  const resolveUser = (id: string | undefined): UserDisplay | undefined =>
    id ? userLookup.data.get(id) : undefined;

  if (periods.length === 0) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-4 text-center dark:border-slate-700 dark:bg-slate-800">
        <h3 className="text-h4 text-slate-900 dark:text-slate-100">
          No history yet
        </h3>
        <p className="mt-1 text-body-sm text-slate-500 dark:text-slate-400">
          Log the first period from the button above to start building the
          trend for this KPI.
        </p>
        {isTruncated && (
          <Alert variant="warning" className="mt-3">
            Older records may not be displayed.
          </Alert>
        )}
      </div>
    );
  }

  const frequency =
    (kpi.measurementFrequency?.toLowerCase() as KpiFrequency) ?? "monthly";
  const visible = showAll ? periods : periods.slice(0, 5);
  const totalLoaded = periods.length;
  const exportLabel = isTruncated
    ? `Export loaded history (${totalLoaded} of ${totalLoaded}+)`
    : "Export CSV";
  const nowIso = isoDateUTC(new Date());
  const filename = `kpi-${slugify(kpi.name)}-history-${range}-${nowIso}.csv`;

  const handleExport = () => {
    const rows: string[] = ["Period,Value,RAG,Logged,Evidence"];
    for (const row of periods) {
      const ext = row.report as ExtendedReport | undefined;
      const evidence = (ext?.evidenceLinks ?? [])
        .map((l) => l.url)
        .join(", ");
      const value = row.measurement?.value ?? "";
      const rag = row.report?.ragRating ?? "";
      const logged = formatLoggedCell(
        row.report,
        resolveUser(row.report?.updatedBy),
      );
      rows.push(
        [row.period, value, rag, logged, evidence]
          .map((v) => `"${v.replace(/"/gu, '""')}"`)
          .join(","),
      );
    }
    try {
      const blob = new Blob([rows.join("\n")], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // Swallow — CSV export is best-effort and not a core flow.
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className="text-h4 text-slate-900 dark:text-slate-100">History</h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleExport}
          data-csv-filename={filename}
          title={
            isTruncated
              ? "Only the first 500 records are loaded — older rows are not included in this export."
              : undefined
          }
        >
          {exportLabel}
        </Button>
      </div>
      {isTruncated && (
        <Alert variant="warning" className="mb-3">
          Older records may not be displayed.
        </Alert>
      )}
      <table
        className="w-full text-body-sm"
        aria-label={`Measurement history for ${kpi.name}`}
      >
        <thead>
          <tr className="border-b border-slate-200 text-caption uppercase tracking-wide text-slate-500 dark:border-slate-700 dark:text-slate-400">
            <th scope="col" className="py-2 pr-3 text-left font-medium">
              Period
            </th>
            <th scope="col" className="py-2 pr-3 text-right font-medium">
              Value
            </th>
            <th scope="col" className="py-2 pr-3 text-left font-medium">
              RAG
            </th>
            <th scope="col" className="py-2 pr-3 text-left font-medium">
              Logged
            </th>
            <th scope="col" className="py-2 pr-3 text-left font-medium">
              Evidence
            </th>
            <th scope="col" className="py-2" />
          </tr>
        </thead>
        <tbody>
          {visible.map(({ period, measurement, report }) => {
            const ext = report as ExtendedReport | undefined;
            const periodLabel = formatPeriodLabel(period, frequency);
            return (
              <tr
                key={period}
                className="border-b border-slate-100 transition-colors last:border-b-0 hover:bg-slate-50 dark:border-slate-700/60 dark:hover:bg-slate-800/50"
                data-testid={`kpi-history-row-${period}`}
                data-period={period}
              >
                <td className="py-2 pr-3 text-left text-slate-900 dark:text-slate-100">
                  {periodLabel}
                </td>
                <td className="py-2 pr-3 text-right text-slate-900 dark:text-slate-100">
                  {formatKpiValue(kpi, measurement).display}
                </td>
                <td className="py-2 pr-3 text-left">
                  {report ? (
                    <TrackingStatusBadge kind="rag" rating={report.ragRating} />
                  ) : (
                    <span aria-label="No RAG rating">—</span>
                  )}
                </td>
                <td
                  className="py-2 pr-3 text-left text-slate-600 dark:text-slate-400"
                  title={fullNameTooltip(resolveUser(report?.updatedBy))}
                >
                  {formatLoggedCell(report, resolveUser(report?.updatedBy))}
                </td>
                <td className="py-2 pr-3 text-left text-slate-600 dark:text-slate-400">
                  <EvidenceLinkDisplay links={ext?.evidenceLinks} />
                </td>
                <td className="py-2 text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    {/* Failing-signal raise: a red-rated period with a
                     *  recorded measurement can spawn a tracked issue auto-
                     *  linked to that measurement (so the back-link badge
                     *  and Linked-to filter surface it immediately). Without
                     *  a measurement (report-only row) we have no kpi-
                     *  measurement target to link, so the affordance hides. */}
                    {report?.ragRating === "red" && measurement?.id && (
                      <RaiseIssueButton
                        contractId={kpi.contractId}
                        srcType="kpi-measurement"
                        srcId={measurement.id}
                        srcTitle={`${kpi.name} — ${periodLabel}`}
                      />
                    )}
                    <Button
                      variant="ghost"
                      iconOnly
                      size="sm"
                      icon={<Pencil className="h-4 w-4" />}
                      aria-label={`Amend ${periodLabel} log`}
                      title="Amend this period"
                      onClick={() =>
                        onAmend({
                          period,
                          value: measurement?.value,
                          ragRating: report?.ragRating,
                          commentary: report?.commentary,
                          evidenceLinks: ext?.evidenceLinks,
                          measurementId: measurement?.id,
                          reportId: report?.id,
                        })
                      }
                    />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {periods.length > 5 && (
        <div className="mt-3 flex items-center justify-between text-caption text-slate-500 dark:text-slate-400">
          <span>
            Showing {visible.length} of {periods.length}
          </span>
          {!showAll && (
            <button
              type="button"
              className="rounded-sm text-brand-600 hover:underline focus:outline-hidden focus:ring-2 focus:ring-brand-400 focus:ring-offset-2 dark:text-brand-400 dark:focus:ring-offset-slate-800"
              onClick={() => setShowAll(true)}
            >
              Show all
            </button>
          )}
        </div>
      )}
    </div>
  );
}
